import { Button, Card, TextField } from '@starter/ui';
import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { authClient } from '../lib/auth.ts';

/**
 * Reached from the emailed link, which goes to the API first — it validates the
 * token and only then redirects here with it in the query. A stale link
 * therefore never reaches this form.
 */
function ResetPassword() {
  const router = useRouter();
  const { token, error: linkError } = Route.useSearch();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) return;

    const form = new FormData(event.currentTarget);
    const newPassword = String(form.get('password') ?? '');
    const confirmation = String(form.get('confirm') ?? '');

    if (newPassword !== confirmation) {
      setError('The two passwords do not match');
      return;
    }

    setPending(true);
    setError(null);
    const { error: resetError } = await authClient.resetPassword({ newPassword, token });
    setPending(false);

    if (resetError) {
      setError(resetError.message ?? 'Could not reset the password');
      return;
    }

    // Every session was revoked with the password (see auth.ts), so there is
    // nothing to invalidate — sign in with the new one.
    await router.navigate({ to: '/login', search: {} });
  }

  if (!token || linkError) {
    return (
      <div className="mx-auto max-w-sm">
        <Card title="That link did not work">
          <div className="flex flex-col gap-4">
            <p className="text-sm text-ink-muted" data-testid="reset-link-invalid">
              A reset link is good for one hour and one use. Ask for a fresh one.
            </p>
            <Button render={<Link to="/forgot-password" />}>Send a new link</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-sm">
      <Card title="Choose a new password">
        <form className="flex flex-col gap-4" onSubmit={onSubmit}>
          <TextField
            label="New password"
            name="password"
            type="password"
            required
            minLength={10}
            autoComplete="new-password"
            description="At least 10 characters."
            data-testid="password"
          />
          <TextField
            label="Repeat the password"
            name="confirm"
            type="password"
            required
            minLength={10}
            autoComplete="new-password"
            data-testid="password-confirm"
          />

          {error ? (
            <p className="text-sm text-danger-text" role="alert" data-testid="auth-error">
              {error}
            </p>
          ) : null}

          <Button type="submit" disabled={pending} data-testid="submit">
            {pending ? 'Saving…' : 'Set the password'}
          </Button>
        </form>
      </Card>
    </div>
  );
}

export const Route = createFileRoute('/reset-password')({
  validateSearch: z.object({
    token: z.string().optional(),
    error: z.string().optional(),
  }),
  component: ResetPassword,
});
